import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  styled,
} from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { getSearchApiBaseUrl } from "../apiConfig";
import { formatPrice } from "../utils/utils";
import { firebaseHelper } from "../utils/firebase";
import { useDesignFirm } from "../utils/useDesignFirm";
import { getAuthToken } from "../utils/auth";
import { getStripePitchImage } from "../utils/stripe-config";
import { getCreditPricesAndLocationAPIUrl } from "../utils/stripe-config";
import { useCreditStore, suppressBalanceListener, OPTIMISTIC_SUPPRESS_MS } from "../utils/useCreditStore";
import { useTeamMembership } from "../utils/useTeamMembership";
import { CreditCheckoutForm } from "./CreditCheckoutForm";

interface BuyCreditsModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Optional user ID - if not provided, will use currently authenticated user
   */
  uid?: string | null;
  /**
   * Optional callback when purchase succeeds
   */
  onSuccess?: (credits: number) => void;
  /**
   * Optional callback when purchase fails
   */
  onError?: (error: string) => void;
  /**
   * Optional callback when user selects a credit pack (click on pack card)
   */
  onPackSelect?: (pack: {
    id: string;
    name: string;
    credits: number;
    price: number;
    currency: string;
    bestValue?: boolean;
  }) => void;
  /**
   * Optional callback when user arrives at the payment screen (after Continue to Payment)
   */
  onPaymentScreenView?: () => void;
  /**
   * Optional analytics tracking function. When provided, the modal will track
   * credit-related events (pack select, payment screen view, purchase success).
   */
  trackEvent?: (event: string, properties?: Record<string, string | number | boolean>) => void;
  /**
   * Optional user role for analytics (e.g. "pro", "basic"). Included in purchase success events.
   */
  userRole?: string;
  /**
   * Optional source identifier for analytics (e.g. "brief_insufficient", "concept_page_insufficient")
   */
  source?: string;
  /**
   * Fetch utility function from the app
   */
  fetchData: (requestData: any, url: string) => Promise<any>;
}

interface CreditPack {
  id: string;
  name: string;
  credits: number;
  price: number;
  currency: string;
  bestValue?: boolean;
}

enum ModalPhase {
  PACK_SELECTION = "pack_selection",
  PAYMENT = "payment",
  SUCCESS = "success",
  ERROR = "error",
}

const StyledDialog = styled(Dialog, {
  shouldForwardProp: (prop: PropertyKey) => prop !== "$isPayment",
})<{ $isPayment?: boolean }>(({ theme, $isPayment }) => ({
  "& .MuiDialog-container": {
    alignItems: "center",
    paddingTop: 0,
  },
  "& .MuiDialog-paper": {
    borderRadius: theme.spacing(2),
    maxWidth: "800px",
    width: "100%",
    padding: 0,
    overflowX: "hidden",
    overflowY: "auto",
    margin: theme.spacing(2),
    marginTop: 0,
    maxHeight: $isPayment ? `calc(100vh - ${theme.spacing(4)})` : "480px",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    [theme.breakpoints.down("md")]: {
      maxWidth: $isPayment
        ? "min(500px, calc(100vw - 24px))"
        : "min(95vw, calc(100vw - 24px))",
      maxHeight: $isPayment ? `calc(100vh - ${theme.spacing(4)})` : "none",
    },
  },
}));

const PackCard = styled(Box)<{ selected?: boolean }>(({ theme, selected }) => ({
  border: selected ? "3px solid #C68CD6" : "2px solid #E5E7EB",
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(1.5, 0.25),
  cursor: "pointer",
  transition: "all 0.2s",
  position: "relative",
  backgroundColor: "white",
  minHeight: "130px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  "&:hover": {
    borderColor: "#C68CD6",
    boxShadow: "0 4px 12px rgba(198, 140, 214, 0.2)",
    transform: "translateY(-2px)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.25, 0.25),
    minHeight: "120px",
  },
}));

const ContinueButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(135deg, #c58ed4, #a251b9)",
  color: "white",
  padding: theme.spacing(1.5, 3),
  fontSize: "1rem",
  fontWeight: "bold",
  borderRadius: "5px",
  textTransform: "none",
  minWidth: "200px",
  "&:hover": {
    background: "linear-gradient(135deg, #b57dc3, #9241a8)",
  },
  "&:disabled": {
    opacity: 0.6,
    color: "white",
  },
  [theme.breakpoints.down("sm")]: {
    minWidth: "auto",
    width: "100%",
    padding: theme.spacing(1.25, 2),
    fontSize: "0.9rem",
  },
}));

export const BuyCreditsModal = ({
  open,
  onClose,
  uid,
  onSuccess: onSuccessCallback,
  onError: onErrorCallback,
  onPackSelect,
  onPaymentScreenView,
  trackEvent,
  userRole,
  source,
  fetchData,
}: BuyCreditsModalProps) => {
  const [phase, setPhase] = useState<ModalPhase>(ModalPhase.PACK_SELECTION);
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [selectedPack, setSelectedPack] = useState<CreditPack | null>(null);
  const [paymentIntent, setPaymentIntent] = useState<{
    clientSecret: string;
    customerSessionClientSecret?: string;
    amount: number;
    currency: string;
    credits: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { fetchBalance, fetchTeamBalance, designFirmId, hideTeamBalance } =
    useCreditStore();
  const useTeamBalance = Boolean(designFirmId && !hideTeamBalance);
  const { teamId, isTeamAdmin, isTeamMember } = useTeamMembership(uid);
  const designFirm = useDesignFirm(firebaseHelper.db, teamId || null);
  const isPurchasingForTeam = isTeamAdmin && useTeamBalance;
  const teamName = designFirm?.firm?.name || "your team";
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refreshBalance = (force = false) => {
    if (useTeamBalance && designFirmId) {
      fetchTeamBalance(designFirmId, force);
      return;
    }
    fetchBalance(force);
  };

  // Fetch packs and track modal open
  useEffect(() => {
    if (open && phase === ModalPhase.PACK_SELECTION) {
      trackEvent?.("Credits - Buy Modal Open", { source: source || '' });
      fetchCreditPacks();
    }
  }, [open, phase]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      // Clear auto-close timer if modal is closed
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
      setPhase(ModalPhase.PACK_SELECTION);
      setSelectedPack(null);
      setPaymentIntent(null);
      setError(null);
    }
  }, [open]);

  const fetchCreditPacks = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch credit prices and location from Firebase Cloud Function
      // This mirrors the subscription flow and properly detects user's IP
      const resp = await fetchData({}, getCreditPricesAndLocationAPIUrl);

      if (!resp?.prices) {
        throw new Error("No credit packs available");
      }

      // Detect user's currency from location data
      const currency = resp?.location?.currency?.code?.toLowerCase() || "usd";
      console.log(
        `[BuyCreditsModal] Detected currency: ${currency.toUpperCase()}`,
      );

      // Process each price to get localized pricing (similar to subscription flow)
      const stripePrices = resp.prices;
      stripePrices.data.forEach((price: any) => {
        if (!price.currency_options) {
          price.local_price = price.unit_amount_decimal || price.unit_amount;
          price.local_currency = price.currency || "usd";
        } else {
          if (price.currency_options[currency]) {
            price.local_price =
              price.currency_options[currency]?.unit_amount_decimal;
            price.local_currency = currency;
          } else {
            // Fallback to USD if user's currency not available
            price.local_price =
              price.currency_options["usd"]?.unit_amount_decimal ||
              price.unit_amount;
            price.local_currency = "usd";
          }
        }
      });

      // Convert Stripe prices to CreditPack format
      const creditPacks = stripePrices.data
        .map((price: any) => {
          const product = price.product;
          const creditAmount = product?.metadata?.credit_amount;
          if (!creditAmount) return null;

          const credits = parseInt(creditAmount, 10);
          const productName = product?.metadata?.product_name;
          const packName = productName
            ? `${productName} Pack: ${credits} Credits`
            : product?.name || `${credits} Credits`;

          return {
            id: price.id,
            name: packName,
            credits: credits,
            price: price.local_price,
            currency: price.local_currency,
            bestValue: product?.metadata?.best_value === "true",
          };
        })
        .filter((pack: any) => pack !== null && pack.credits > 0)
        .sort((a: any, b: any) => a.credits - b.credits);

      setPacks(creditPacks);
    } catch (err) {
      console.error("[BuyCreditsModal] Fetch packs error:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load credit packs";
      setError(errorMessage);
      setPhase(ModalPhase.ERROR);

      // Notify parent component of error
      if (onErrorCallback) {
        onErrorCallback(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleContinueToPayment = async () => {
    if (!selectedPack) return;

    setLoading(true);
    setError(null);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error("Authentication required");
      }

      // Determine endpoint based on team admin status
      let endpoint: string;
      if (isPurchasingForTeam && designFirmId) {
        endpoint = `${getSearchApiBaseUrl()}/api/credits/team/${designFirmId}/purchase/intent`;
      } else {
        endpoint = `${getSearchApiBaseUrl()}/api/credits/purchase/intent`;
      }

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          priceId: selectedPack.id,
          currency: selectedPack.currency,
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json();

        // Handle specific team errors
        if (response.status === 403) {
          throw new Error("You must be a team admin to purchase team credits");
        }
        if (errorData.error === "TEAM_STRIPE_CUSTOMER_NOT_CONFIGURED") {
          throw new Error(
            "Team billing not configured. Please contact support.",
          );
        }

        throw new Error(errorData.error || "Failed to initialize payment");
      }

      const data = await response.json();
      setPaymentIntent({
        clientSecret: data.clientSecret,
        customerSessionClientSecret: data.customerSessionClientSecret,
        amount: data.amount,
        currency: data.currency,
        credits: data.credits,
      });
      setPhase(ModalPhase.PAYMENT);
      onPaymentScreenView?.();
      trackEvent?.("Credits - Payment Screen View");
    } catch (err) {
      clearTimeout(timeoutId);
      console.error("[BuyCreditsModal] Create payment intent error:", err);

      let errorMessage: string;
      if (err instanceof Error && err.name === "AbortError") {
        errorMessage =
          "Request timed out. Please check your connection and try again.";
      } else {
        errorMessage =
          err instanceof Error ? err.message : "Failed to initialize payment";
      }
      setError(errorMessage);
      setPhase(ModalPhase.ERROR);

      // Notify parent component of error
      if (onErrorCallback) {
        onErrorCallback(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    setPhase(ModalPhase.SUCCESS);

    // Suppress Firestore listener for 5s so it doesn't overwrite the optimistic value
    // with stale data before the webhook has processed.
    suppressBalanceListener();

    // Optimistic update: immediately reflect purchased credits in the UI
    if (selectedPack) {
      const store = useCreditStore.getState();
      if (useTeamBalance && store.teamBalance) {
        useCreditStore.setState({
          teamBalance: {
            ...store.teamBalance,
            totalBalance: store.teamBalance.totalBalance + selectedPack.credits,
          },
        });
      } else if (store.balance) {
        useCreditStore.setState({
          balance: {
            ...store.balance,
            totalBalance: store.balance.totalBalance + selectedPack.credits,
            stripeBalance:
              (store.balance.stripeBalance || 0) + selectedPack.credits,
          },
        });
      }
    }

    // Delayed authoritative sync — by 5s the webhook has granted credits in Stripe
    setTimeout(() => {
      refreshBalance(true);
    }, OPTIMISTIC_SUPPRESS_MS);

    // Notify parent component of success
    if (selectedPack) {
      trackEvent?.("Credits - Purchase Success", {
        credits: selectedPack.credits,
        userRole: userRole || '',
      });
      onSuccessCallback?.(selectedPack.credits);
    }

    // Clear any existing timer before starting a new one
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
    }

    // Auto-close after 3 seconds
    autoCloseTimerRef.current = setTimeout(() => {
      onClose();
    }, 3000);
  };

  const handlePaymentError = (errorMessage: string) => {
    // Payment validation errors are shown inline in CreditCheckoutForm.
    // Only notify parent — don't switch to the error phase.
    if (onErrorCallback) {
      onErrorCallback(errorMessage);
    }
  };

  const handleBack = () => {
    setPhase(ModalPhase.PACK_SELECTION);
    setSelectedPack(null);
    setPaymentIntent(null);
    setError(null);
  };

  const handleTryAgain = () => {
    setPhase(ModalPhase.PACK_SELECTION);
    setSelectedPack(null);
    setPaymentIntent(null);
    setError(null);
    fetchCreditPacks();
  };

  const renderPackSelection = () => (
    <Box>
      {/* Header */}
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ alignItems: "flex-start", mb: 0.5 }}
      >
        <BoltIcon
          sx={{
            color: "#C68CD6",
            fontSize: { xs: "1.75rem", sm: "2rem" },
            mt: 0.25,
          }}
        />
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              mb: 0.25,
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
            }}
          >
            {isPurchasingForTeam
              ? `Buy Credits for ${teamName}`
              : "Buy Credits"}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { xs: "0.8125rem", sm: "0.875rem" } }}
          >
            {isPurchasingForTeam
              ? "Purchase credits for your team. One-time packs that never expire."
              : "One-time packs that never expire."}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ mt: -0.5 }}>
          <CloseIcon />
        </IconButton>
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Credit Packs */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: `repeat(${Math.min(packs.length, 3)}, 230px)`,
              },
              gap: { xs: 1, sm: 1.5, md: 1.5 },
              my: { xs: 1.5, sm: 2 },
              justifyContent: "center",
            }}
          >
            {packs.map((pack) => (
              <PackCard
                key={pack.id}
                data-testid="credit-pack-card"
                selected={selectedPack?.id === pack.id}
                onClick={() => {
                  setSelectedPack(pack);
                  onPackSelect?.(pack);
                  trackEvent?.("Credits - Pack Select", {
                    packId: pack.id,
                    packName: pack.name,
                    credits: pack.credits,
                    price: pack.price,
                    currency: pack.currency,
                    bestValue: pack.bestValue ?? false,
                  });
                }}
              >
                {pack.bestValue && (
                  <Chip
                    label="Best Value"
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      background: "linear-gradient(135deg, #c58ed4, #a251b9)",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "0.7rem",
                      height: "20px",
                    }}
                  />
                )}

                {selectedPack?.id === pack.id && (
                  <CheckCircleIcon
                    sx={{
                      position: "absolute",
                      bottom: 8,
                      right: 8,
                      color: "#C68CD6",
                      fontSize: "1.25rem",
                    }}
                  />
                )}

                <Stack
                  spacing={0.5}
                  sx={{ textAlign: "center", alignItems: "center" }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                  >
                    {pack.name.replace(" Pack:", "").replace(" Credits", "")}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ alignItems: "center" }}
                  >
                    <BoltIcon
                      sx={{
                        color: "#F59E0B",
                        fontSize: { xs: "1.25rem", sm: "1.5rem" },
                      }}
                    />
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: "bold",
                        color: "#667eea",
                        fontSize: { xs: "1.75rem", sm: "2rem" },
                      }}
                    >
                      {pack.credits}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                    >
                      Credits
                    </Typography>
                  </Stack>

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "1rem", sm: "1.25rem" },
                    }}
                  >
                    {formatPrice(pack.price, pack.currency, true)}
                  </Typography>
                </Stack>
              </PackCard>
            ))}
          </Box>

          {/* What you get section */}
          <Box
            sx={{
              bgcolor: "#F9FAFB",
              borderRadius: 1.5,
              p: { xs: 1.5, sm: 2 },
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: "bold",
                mb: 0.75,
                fontSize: { xs: "0.8125rem", sm: "0.875rem" },
              }}
            >
              What you get:
            </Typography>
            <Stack spacing={0.25}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: "0.8125rem", sm: "0.875rem" } }}
              >
                - Credits never expire
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: "0.8125rem", sm: "0.875rem" } }}
              >
                - Instant delivery
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: "0.8125rem", sm: "0.875rem" } }}
              >
                - Secure payment via Stripe
              </Typography>
            </Stack>
          </Box>

          {/* Action buttons */}
          <Stack
            direction={{ xs: "column-reverse", sm: "row" }}
            spacing={{ xs: 1, sm: 2 }}
            sx={{ justifyContent: "center", mt: { xs: 1.5, sm: 2 } }}
          >
            <Button
              onClick={onClose}
              variant="text"
              sx={{
                textTransform: "none",
                color: "text.secondary",
                fontSize: { xs: "0.85rem", sm: "0.9rem" },
              }}
            >
              Cancel
            </Button>
            <ContinueButton
              onClick={handleContinueToPayment}
              disabled={!selectedPack || loading}
              startIcon={<BoltIcon />}
            >
              Continue to Payment
            </ContinueButton>
          </Stack>
        </>
      )}
    </Box>
  );

  const renderPayment = () => (
    <Box>
      {/* Header - matches subscription payment style */}
      <Stack direction="row" sx={{ alignItems: "center" }}>
        <IconButton
          onClick={handleBack}
          disabled={loading}
          aria-label="Back to packs"
        >
          <ChevronLeftIcon />
        </IconButton>
        <Typography
          sx={{ fontSize: "25px", lineHeight: "115%", paddingBottom: "0px" }}
        >
          Complete Payment
        </Typography>
      </Stack>

      {/* Pack Info - matches subscription total amount style */}
      {selectedPack && (
        <Box sx={{ pt: "20px", pb: "20px" }}>
          <Stack spacing={1}>
            <Stack
              spacing={2}
              direction="row"
              sx={{ justifyContent: "space-between", alignItems: "center" }}
            >
              <Typography sx={{ fontSize: "0.9rem" }}>Pack:</Typography>
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 500 }}>
                {selectedPack.credits} Credits
              </Typography>
            </Stack>
            <Stack
              spacing={2}
              direction="row"
              sx={{ justifyContent: "space-between", alignItems: "center" }}
            >
              <Typography sx={{ fontSize: "0.9rem" }}>Total Amount:</Typography>
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 500 }}>
                {formatPrice(selectedPack.price, selectedPack.currency, true)}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      )}

      {/* Payment Form */}
      {paymentIntent && (
        <CreditCheckoutForm
          clientSecret={paymentIntent.clientSecret}
          amount={paymentIntent.amount}
          credits={paymentIntent.credits}
          currency={paymentIntent.currency}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
        />
      )}
    </Box>
  );

  const renderSuccess = () => (
    <Box>
      <Stack
        spacing={3}
        sx={{ py: 6, alignItems: "center", textAlign: "center" }}
      >
        <CheckCircleIcon sx={{ fontSize: "5rem", color: "#10B981" }} />
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          Payment Successful!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {isPurchasingForTeam
            ? `${selectedPack?.credits} credits have been added to ${teamName}'s account.`
            : `${selectedPack?.credits} credits have been added to your account.`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This window will close automatically...
        </Typography>
      </Stack>
    </Box>
  );

  const renderError = () => (
    <Box>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: "bold", flex: 1 }}>
          Error
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Stack>

      <Stack spacing={3}>
        <Alert severity="error">{error || "An error occurred"}</Alert>

        <Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end" }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Close
          </Button>
          <Button
            onClick={handleTryAgain}
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #c58ed4, #a251b9)",
              textTransform: "none",
              borderRadius: "5px",
              "&:hover": {
                background: "linear-gradient(135deg, #b57dc3, #9241a8)",
              },
            }}
          >
            Try Again
          </Button>
        </Stack>
      </Stack>
    </Box>
  );

  const isPaymentPhase = phase === ModalPhase.PAYMENT;

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      $isPayment={isPaymentPhase}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isPaymentPhase
            ? { xs: "column", md: "row" }
            : "column",
          width: "100%",
          minHeight: isPaymentPhase ? { md: "min(85vh, 800px)" } : "auto",
          position: "relative",
          maxHeight: "calc(100vh - 32px)",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {/* Main Content */}
        <Box
          sx={{
            width: isPaymentPhase ? { xs: "100%", md: "400px" } : "100%",
            flexShrink: 0,
            p: isPaymentPhase ? "15px" : { xs: 1, sm: 1.5, md: 2 },
            overflowY: "auto",
            maxHeight: isPaymentPhase ? { md: "min(85vh, 800px)" } : "none",
            boxSizing: "border-box",
          }}
        >
          {phase === ModalPhase.PACK_SELECTION && renderPackSelection()}
          {phase === ModalPhase.PAYMENT && renderPayment()}
          {phase === ModalPhase.SUCCESS && renderSuccess()}
          {phase === ModalPhase.ERROR && renderError()}
        </Box>

        {/* Image Section - Only shown in payment phase on desktop */}
        {isPaymentPhase && (
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flex: 1,
              position: "relative",
              bgcolor: "#111827",
              minHeight: "600px",
            }}
          >
            <Box
              component="img"
              src={getStripePitchImage("width=900,format=auto")}
              alt="Mattoboard preview"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              loading="lazy"
            />
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.25) 100%)",
              }}
            />
          </Box>
        )}
      </Box>
    </StyledDialog>
  );
};
